import { FanNumberingDashboardService, type TeamMember } from '@api'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { matchPath } from 'react-router'

import { Pages } from '@/consts/routes'
import { $creatorId } from '@/models/creators'
import { initializeApp } from '@/models/init'
import { $currentPage } from '@/models/router'
import { toast } from '@/models/toasts'
import { handleError } from '@/utils/error'

export const fetchSupervisorsFx = createEffect({
  name: 'fetchSupervisors',
  handler: async (creatorId: number) => {
    const response = await FanNumberingDashboardService.getSupervisors({
      creatorId,
    })

    return response.supervisor || []
  },
})

fetchSupervisorsFx.fail.watch(({ error, params }) => {
  handleError(error, 'Failed to fetch supervisors', () =>
    fetchSupervisorsFx(params),
  )
})

export const setSupervisorsFx = createEffect({
  name: 'setSupervisors',
  handler: async ({
    creatorId,
    supervisors,
  }: {
    creatorId: number
    supervisors: number[]
  }) => {
    return FanNumberingDashboardService.setSupervisors({
      creatorId,
      requestBody: { supervisors },
    })
  },
})

setSupervisorsFx.failData.watch((error) => {
  handleError(error, 'Failed to set supervisors')
})

setSupervisorsFx.doneData.watch(() => {
  toast.success(
    'Successfully saved. Changes will be applied within a few minutes',
  )
})

export const updateSupervisors = createEvent<number[]>({
  name: 'updateSupervisors',
})
export const resetSupervisors = createEvent({
  name: 'resetSupervisors',
})

export const $supervisors = createStore<TeamMember[]>([], {
  name: '$supervisors',
})
  .on(fetchSupervisorsFx.doneData, (_, payload) => payload)
  .reset(resetSupervisors)

sample({
  clock: [$currentPage, initializeApp, $creatorId],
  source: { page: $currentPage, creatorId: $creatorId },
  filter: ({ page, creatorId }) => {
    const isFanNumberingPage = matchPath(
      Pages.CREATOR_SETTINGS_FAN_NUMBERING,
      page,
    )

    return Boolean(isFanNumberingPage && creatorId)
  },
  fn: ({ creatorId }) => creatorId!,
  target: fetchSupervisorsFx,
})

sample({
  clock: updateSupervisors,
  source: $creatorId,
  filter: Boolean,
  fn: (creatorId, supervisors) => ({ creatorId, supervisors }),
  target: setSupervisorsFx,
})

sample({
  clock: setSupervisorsFx.doneData,
  source: $creatorId,
  filter: Boolean,
  target: fetchSupervisorsFx,
})
